import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GenericChatService, ChatMessage, ChatConfig, ChatSession } from '../services/genericChatService';
import { aiCharacterService, AICharacter } from '../services/aiCharacterService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './FloatingChat.css';

const FloatingChat: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const [characters, setCharacters] = useState<AICharacter[]>([]);
  const [loadingCharacters, setLoadingCharacters] = useState(true);
  const [chatConfig, setChatConfig] = useState<ChatConfig>({
    provider: 'claude',
    model: 'claude-haiku-4-5'
  });
  const [selectedCharacter, setSelectedCharacter] = useState<AICharacter | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load AI characters when component mounts
  useEffect(() => {
    if (user) {
      loadCharacters();
    }
  }, [user]);

  // Load chat history when session changes
  useEffect(() => {
    if (user && currentSessionId) {
      loadSession(currentSessionId);
    } else if (user && !currentSessionId && isOpen) {
      initializeWelcomeMessage();
    }
  }, [currentSessionId, user, isOpen]);

  const loadCharacters = async () => {
    if (!user) return;
    
    setLoadingCharacters(true);
    try {
      const response = await aiCharacterService.getUserCharacters({
        user_id: user.id.toString(),
        is_active: true
      });
      if (response.success && response.characters) {
        setCharacters(response.characters);
      }
    } catch (error) {
      console.error('Error loading AI characters:', error);
    } finally {
      setLoadingCharacters(false);
    }
  };

  const loadSession = async (sessionId: number) => {
    if (!user) return;
    
    try {
      const { session, messages: sessionMessages } = await GenericChatService.loadSession(sessionId);
      
      // Update chat config based on session
      if (session.character_id) {
        if (characters.length === 0) {
          await loadCharacters();
        }
        const character = characters.find(c => c.id === session.character_id);
        if (character) {
          setSelectedCharacter(character);
          setChatConfig({ characterId: character.id });
        } else {
          setSelectedCharacter(null);
          setChatConfig({
            provider: 'claude',
            model: 'claude-haiku-4-5'
          });
        }
      } else if (session.provider && session.model) {
        setSelectedCharacter(null);
        setChatConfig({
          provider: session.provider,
          model: session.model
        });
      }
      
      setMessages(sessionMessages);
      setCurrentSessionId(sessionId);
      
      if (sessionMessages.length === 0) {
        initializeWelcomeMessage();
      }
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const createNewSession = async () => {
    if (!user || !chatConfig) return;
    
    try {
      const session = await GenericChatService.createSession(chatConfig);
      setCurrentSessionId(session.id);
      setMessages([]);
      initializeWelcomeMessage();
    } catch (error) {
      console.error('Error creating new session:', error);
    }
  };

  const initializeWelcomeMessage = async (config?: ChatConfig) => {
    const configToUse = config || chatConfig;
    if (!user || !configToUse) return;
    
    try {
      const userName = user.display_name || user.username;
      const welcomeText = await GenericChatService.generateWelcomeMessage(configToUse, userName);
      const welcomeMessage = GenericChatService.createMessage(welcomeText, 'assistant');
      setMessages([welcomeMessage]);
    } catch (error) {
      console.error('Error initializing welcome message:', error);
    }
  };

  const handleConfigChange = async (newConfig: ChatConfig, oldConfig?: ChatConfig) => {
    const configToUseForSave = oldConfig || chatConfig;
    
    // Save current session if it exists
    if (currentSessionId && messages.length > 0) {
      try {
        const messagesToSave = messages.filter(msg => msg.role !== 'assistant' || !msg.content.includes('Hello'));
        if (messagesToSave.length > 0) {
          await GenericChatService.saveChatHistory(configToUseForSave, messagesToSave, currentSessionId);
        }
      } catch (error) {
        console.error('Error saving session:', error);
      }
    }

    // Create new session for new config
    try {
      const session = await GenericChatService.createSession(newConfig);
      setCurrentSessionId(session.id);
      setChatConfig(newConfig);
      setMessages([]);
      setTimeout(() => {
        initializeWelcomeMessage(newConfig);
      }, 100);
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const handleCharacterSelect = async (value: string) => {
    const oldConfig = { ...chatConfig };
    
    if (value === 'default') {
      const newConfig: ChatConfig = {
        provider: 'claude',
        model: 'claude-haiku-4-5'
      };
      setSelectedCharacter(null);
      setChatConfig(newConfig);
      await handleConfigChange(newConfig, oldConfig);
    } else if (value === 'default-gemini') {
      const newConfig: ChatConfig = {
        provider: 'gemini',
        model: 'gemini-2.5-pro'
      };
      setSelectedCharacter(null);
      setChatConfig(newConfig);
      await handleConfigChange(newConfig, oldConfig);
    } else if (value === 'default-grok') {
      const newConfig: ChatConfig = {
        provider: 'grok',
        model: 'grok-4-1-fast-non-reasoning-latest'
      };
      setSelectedCharacter(null);
      setChatConfig(newConfig);
      await handleConfigChange(newConfig, oldConfig);
    } else {
      const characterId = parseInt(value);
      const character = characters.find(c => c.id === characterId);
      if (character) {
        const newConfig: ChatConfig = {
          characterId: character.id
        };
        setSelectedCharacter(character);
        setChatConfig(newConfig);
        await handleConfigChange(newConfig, oldConfig);
      }
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !chatConfig || !user) return;

    // Create session if it doesn't exist
    let sessionId = currentSessionId;
    if (!sessionId) {
      try {
        const session = await GenericChatService.createSession(chatConfig);
        sessionId = session.id;
        setCurrentSessionId(sessionId);
      } catch (error) {
        console.error('Error creating session:', error);
        return;
      }
    }

    const userMessage = GenericChatService.createMessage(inputMessage, 'user');
    const currentMessages = [...messages, userMessage];
    
    setMessages(currentMessages);
    setInputMessage('');
    setIsTyping(true);
    
    // Save user message to session
    try {
      await GenericChatService.saveMessage(sessionId, userMessage);
    } catch (error) {
      console.error('Error saving user message:', error);
    }

    try {
      const contextMessages = messages.filter(msg => msg.role !== 'assistant' || !msg.content.includes('Hello'));
      
      const response = await GenericChatService.sendMessage(
        inputMessage,
        chatConfig,
        contextMessages,
        user.id
      );

      const aiMessage = GenericChatService.createMessage(
        response.response,
        'assistant',
        response.images,
        response.toolCalls
      );
      
      const newMessages = [...currentMessages, aiMessage];
      setMessages(newMessages);
      
      // Save AI message to session
      try {
        await GenericChatService.saveMessage(sessionId, aiMessage);
      } catch (error) {
        console.error('Error saving AI message:', error);
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage = GenericChatService.createMessage(
        'I apologize, but I encountered an error. Please try again.',
        'assistant'
      );
      const newMessages = [...currentMessages, errorMessage];
      setMessages(newMessages);
      
      try {
        await GenericChatService.saveMessage(sessionId, errorMessage);
      } catch (error) {
        console.error('Error saving error message:', error);
      }
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Load saved position from localStorage on mount
  useEffect(() => {
    const savedPosition = localStorage.getItem('floatingChatButtonPosition');
    if (savedPosition) {
      try {
        const { x, y } = JSON.parse(savedPosition);
        setPosition({ x, y });
      } catch (error) {
        console.error('Error loading saved position:', error);
      }
    } else {
      // Default position (bottom left)
      const defaultX = 1 * 16; // left margin
      const defaultY = window.innerHeight - 3.5 * 16 - 1 * 16; // button height + bottom margin
      setPosition({ x: defaultX, y: defaultY });
    }
  }, []);

  // Save position to localStorage whenever it changes
  useEffect(() => {
    if (position.x !== 0 || position.y !== 0) {
      localStorage.setItem('floatingChatButtonPosition', JSON.stringify(position));
    }
  }, [position]);

  // Handle window resize to keep button within bounds
  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - 3.5 * 16;
      const maxY = window.innerHeight - 3.5 * 16;
      
      setPosition(prev => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY))
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle drag start (mouse)
  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left - rect.width / 2,
        y: e.clientY - rect.top - rect.height / 2
      });
      dragStartPosRef.current = { x: position.x, y: position.y };
      setIsDragging(true);
      setHasMoved(false);
    }
  };

  // Handle drag start (touch)
  const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    if (buttonRef.current && e.touches.length === 1) {
      const touch = e.touches[0];
      const rect = buttonRef.current.getBoundingClientRect();
      setDragOffset({
        x: touch.clientX - rect.left - rect.width / 2,
        y: touch.clientY - rect.top - rect.height / 2
      });
      dragStartPosRef.current = { x: position.x, y: position.y };
      setIsDragging(true);
      setHasMoved(false);
      e.preventDefault();
    }
  };

  // Handle drag (both mouse and touch)
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      const moved = Math.abs(newX - dragStartPosRef.current.x) > 5 || 
                    Math.abs(newY - dragStartPosRef.current.y) > 5;
      if (moved) {
        setHasMoved(true);
      }
      
      const maxX = window.innerWidth - 3.5 * 16;
      const maxY = window.innerHeight - 3.5 * 16;
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        const newX = touch.clientX - dragOffset.x;
        const newY = touch.clientY - dragOffset.y;
        
        const moved = Math.abs(newX - dragStartPosRef.current.x) > 5 || 
                      Math.abs(newY - dragStartPosRef.current.y) > 5;
        if (moved) {
          setHasMoved(true);
          e.preventDefault();
        }
        
        const maxX = window.innerWidth - 3.5 * 16;
        const maxY = window.innerHeight - 3.5 * 16;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setTimeout(() => setHasMoved(false), 50);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      setTimeout(() => setHasMoved(false), 300);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isDragging, dragOffset]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsOpen(false);
    }
  };

  // Don't render if no user
  if (!user) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <button
          ref={buttonRef}
          className={`floating-chat-button ${isDragging ? 'dragging' : ''}`}
          onClick={(e) => {
            if (!hasMoved && !isDragging) {
              setIsOpen(true);
            }
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          aria-label="Open chat"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            right: 'auto',
            bottom: 'auto'
          }}
        >
          💬
        </button>
      )}

      {isOpen && (
        <>
          <div 
            className="floating-chat-overlay"
            onClick={handleOverlayClick}
          />
          <div className="floating-chat-modal">
            <div className="floating-chat-header">
              <h2 className="floating-chat-title">/chat</h2>
              <button
                className="floating-chat-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Character Selector */}
            <div className="floating-chat-selector">
              <select
                className="floating-chat-selector-dropdown"
                value={selectedCharacter ? selectedCharacter.id.toString() : (chatConfig.provider === 'gemini' ? 'default-gemini' : chatConfig.provider === 'grok' ? 'default-grok' : 'default')}
                onChange={(e) => handleCharacterSelect(e.target.value)}
                disabled={isTyping || loadingCharacters}
              >
                <option value="default">Default: Claude</option>
                <option value="default-gemini">Default: Gemini</option>
                <option value="default-grok">Default: Grok</option>
                {characters.map(character => (
                  <option key={character.id} value={character.id}>
                    {character.name} ({character.model_provider})
                  </option>
                ))}
              </select>
              {selectedCharacter && (
                <button
                  className="floating-chat-edit-character-btn"
                  onClick={() => {
                    navigate(`/ai-characters/${selectedCharacter.id}/edit`);
                    setIsOpen(false);
                  }}
                  disabled={isTyping}
                  title="Edit character"
                >
                  ✏️
                </button>
              )}
            </div>

            {/* Chat Messages */}
            <div className="floating-chat-messages">
              {messages.map((message) => (
                <div key={message.id} className={`floating-chat-message ${message.role}`}>
                  <div className="floating-chat-message-content">
                    <div className="floating-chat-message-text">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.content}
                      </ReactMarkdown>
                    </div>
                    {message.images && message.images.length > 0 && (
                      <div className="floating-chat-message-images">
                        {message.images.map((image, index) => (
                          <div key={index} className="floating-chat-image-container">
                            <img
                              src={`data:${image.mimeType};base64,${image.data}`}
                              alt={image.alt || 'Generated illustration'}
                              className="floating-chat-image"
                              loading="lazy"
                              onError={(e) => {
                                console.error('Failed to load generated image:', e);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                            {image.alt && (
                              <div className="floating-chat-image-caption">{image.alt}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="floating-chat-message-time">
                    {message.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="floating-chat-message assistant typing">
                  <div className="floating-chat-message-content">
                    <div className="floating-chat-typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <div className="floating-chat-input-area">
              <div className="floating-chat-input-container">
                <input
                  ref={chatInputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="floating-chat-input"
                  disabled={isTyping}
                />
                <button 
                  onClick={sendMessage} 
                  disabled={!inputMessage.trim() || isTyping}
                  className="floating-chat-send-btn"
                >
                  ➤
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FloatingChat;

